import React, { memo, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Icons, PageTitle } from '@components/common';
import Switcher from '@components/common/Switcher';
import AlarmByVendor from '@components/routes/AdminPanel/Alarms/AlarmByVendor';

import { useQueryAlarmVendors } from '@hooks/alarms/useQueryAlarmVendors';

import { ISwitcherOption } from '@components/common/Switcher/interfaces';

import { EAlarmManagementView } from '@enums/alarms';
import { EAdminPanelPageTitles, EVendorsValues } from '@enums/common';

import styles from './Alarms.module.scss';

const VIEW_OPTIONS: ISwitcherOption[] = [
	{ id: 'oran-compliant', value: EAlarmManagementView.combined, label: 'O-RAN-compliant' },
	{ id: 'per-vendor', value: EAlarmManagementView.perVendor, label: 'Per-vendor' },
];

const MIN_ORAN_VENDORS_FOR_COMBINED_VIEW = 2;

const Alarms = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const { alarmVendors } = useQueryAlarmVendors();

	const viewParam = searchParams.get('view');

	const oranCompliantVendors = useMemo(() => {
		return alarmVendors.filter(v =>
			v.name === EVendorsValues.oran || v.name === EVendorsValues.benetel,
		);
	}, [alarmVendors]);

	const shouldShowSwitcher = oranCompliantVendors.length >= MIN_ORAN_VENDORS_FOR_COMBINED_VIEW;

	const selectedView = useMemo(() => {
		const view = VIEW_OPTIONS.find(opt => opt.value === viewParam);

		return view || VIEW_OPTIONS[0];
	}, [viewParam]);

	const handleViewChange = (option: ISwitcherOption) => {
		setSearchParams({ view: option.value });
	};

	useEffect(() => {
		if (!shouldShowSwitcher && viewParam === EAlarmManagementView.combined) {
			setSearchParams({ view: EAlarmManagementView.perVendor });
		}
	}, [shouldShowSwitcher, viewParam, setSearchParams]);

	return (
		<div className={styles.alarmsWrapper}>
			<div className={styles.headerContainer}>
				<PageTitle
					icon={Icons.alarmInverse()}
					title={EAdminPanelPageTitles.Alarms}
				/>
				{shouldShowSwitcher && (
					<Switcher
						handleChange={handleViewChange}
						options={VIEW_OPTIONS}
						value={selectedView}
					/>
				)}
			</div>
			<div className={styles.whiteWrapper}>
				<AlarmByVendor selectedView={selectedView.value} />
			</div>
		</div>
	);
};

export default memo(Alarms);
